import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Plus, X, Edit, Trash2, Check, Clock, AlertCircle, Search, Filter } from 'lucide-react';
import SearchableDropdown from './SearchableDropdown';
import './RBACManagement.css';

const RBACManagement = () => {
  const { authState } = useOktaAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [rbacEntries, setRbacEntries] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [systems, setSystems] = useState([]);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [functionalRoleResources, setFunctionalRoleResources] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('All Teams');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [formData, setFormData] = useState({
    resource: '',
    teamId: '',
    roleId: '',
    systemId: '',
    template: '',
    functionalRole: '',
    newAD_Group: true,
    expectedAccess: 'Day 1',
    dependencies: '',
    comments: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchRBACEntries(),
        fetchTeams(),
        fetchRoles(),
        fetchSystems(),
        fetchFunctionalRoles(),
        fetchFunctionalRoleResources(),
        fetchResources()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRBACEntries = async () => {
    try {
      const response = await fetch('/api/rbac');
      if (response.ok) {
        const data = await response.json();
        setRbacEntries(data);
      }
    } catch (error) {
      console.error('Error fetching RBAC entries:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams');
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles');
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchSystems = async () => {
    try {
      const response = await fetch('/api/systems');
      if (response.ok) {
        const data = await response.json();
        setSystems(data);
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  };

  const fetchFunctionalRoles = async () => {
    try {
      const response = await fetch('/api/subroles');
      if (response.ok) {
        const data = await response.json();
        setFunctionalRoles(data);
      }
    } catch (error) {
      console.error('Error fetching functional roles:', error);
      setFunctionalRoles([]);
    }
  };

  const fetchFunctionalRoleResources = async () => {
    try {
      const response = await fetch('/api/subrole-resources');
      if (response.ok) {
        const data = await response.json();
        setFunctionalRoleResources(data);
      }
    } catch (error) {
      console.error('Error fetching functional role resources:', error);
      setFunctionalRoleResources([]);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch('/api/resources');
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/rbac', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          modifiedBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Refresh the entries list to get the newly added entry
        await fetchRBACEntries();
        setFormData({
          resource: '',
          teamId: '',
          roleId: '',
          systemId: '',
          template: '',
          functionalRole: '',
          newAD_Group: true,
          expectedAccess: 'Day 1',
          dependencies: '',
          comments: ''
        });
        setShowAddForm(false);
        alert('RBAC entry added successfully!');
      } else {
        const error = await response.json();
        alert('Error adding RBAC entry: ' + error.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error adding RBAC entry');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setFormData({
      resource: '',
      teamId: '',
      roleId: '',
      systemId: '',
      template: '',
      functionalRole: '',
      newAD_Group: true,
      expectedAccess: 'Day 1',
      dependencies: '',
      comments: ''
    });
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch(`/api/rbac/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          approvedBy: currentUser
        }),
      });

      if (response.ok) {
        setRbacEntries(prev => prev.map(entry => 
          entry.id === id 
            ? { ...entry, Status: newStatus, ApprovedBy: currentUser, ApprovedDate: new Date().toLocaleString() }
            : entry
        ));
      } else {
        alert('Error updating status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RBAC entry?')) {
      return;
    }

    try {
      const response = await fetch(`/api/rbac/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setRbacEntries(prev => prev.filter(entry => entry.id !== id));
      } else {
        alert('Error deleting entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <Check size={16} className="status-icon approved" />;
      case 'Rejected':
        return <X size={16} className="status-icon rejected" />;
      case 'Pending':
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Approved':
        return 'status-badge status-approved';
      case 'Rejected':
        return 'status-badge status-rejected';
      case 'Pending':
      default:
        return 'status-badge status-pending';
    }
  };

  // Filter roles based on selected team for form
  const filteredRoles = formData.teamId 
    ? roles.filter(role => String(role.TeamID) === String(formData.teamId))
    : roles;

  // Get existing resource names for dropdown
  const resourceOptions = resources.map(resource => resource.AccessResource).filter(Boolean);

  // Get functional role names for dropdown
  const functionalRoleOptions = functionalRoles.map(functionalRole => functionalRole.name).filter(Boolean);

  // Get all unique existing resources from RBAC entries as well
  const existingRbacResources = [...new Set(rbacEntries.map(entry => entry.Resource))].filter(Boolean);
  
  // Combine all resource options
  const allResourceOptions = [...new Set([...resourceOptions, ...existingRbacResources])].sort();


  // Combine RBAC entries with functional role resources
  const combinedEntries = [
    ...rbacEntries,
    ...functionalRoleResources.map(frResource => {
      const functionalRole = functionalRoles.find(fr => fr.id === frResource.subroleId);
      const parentRole = roles.find(r => r.RoleID === functionalRole?.parentRoleId);
      const parentTeam = teams.find(t => t.TeamID === parentRole?.TeamID);
      
      return {
        id: `fr-${frResource.id}`,
        Resource: frResource.resourceName,
        TeamName: parentTeam?.Title || 'Unknown Team',
        RoleName: `${parentRole?.Title || 'Unknown Role'} (${functionalRole?.name || 'Unknown Functional Role'})`,
        SystemName: frResource.TargetSystem,
        Template: 'Functional Role Assignment',
        ExpectedAccess: 'As Assigned',
        Status: 'Approved',
        ModifiedDate: frResource.assignedDate || new Date().toLocaleDateString(),
        ModifiedBy: frResource.assignedBy || 'System',
        isFunctionalRole: true,
        functionalRoleName: functionalRole?.name,
        DataRisk: frResource.DataRisk,
        AccessEnvironment: frResource.AccessEnvironment
      };
    })
  ];

  // Filter and sort combined entries based on search and filters
  const filteredRbacEntries = combinedEntries.filter(entry => {
    const matchesSearch = !searchQuery || 
      entry.Resource.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.TeamName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.RoleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.SystemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (entry.Template && entry.Template.toLowerCase().includes(searchQuery.toLowerCase())) ||
      entry.ModifiedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (entry.functionalRoleName && entry.functionalRoleName.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTeam = selectedTeam === 'All Teams' || 
      entry.TeamName === selectedTeam;
    
    const matchesRole = selectedRole === 'All Roles' || 
      entry.RoleName === selectedRole || 
      (entry.functionalRoleName && entry.functionalRoleName === selectedRole);
    
    const matchesStatus = selectedStatus === 'All Status' || 
      entry.Status === selectedStatus;
    
    return matchesSearch && matchesTeam && matchesRole && matchesStatus;
  }).sort((a, b) => {
    // Primary sort: Team name
    const teamComparison = a.TeamName.localeCompare(b.TeamName);
    if (teamComparison !== 0) return teamComparison;
    
    // Secondary sort: Role name
    const roleComparison = a.RoleName.localeCompare(b.RoleName);
    if (roleComparison !== 0) return roleComparison;
    
    // Tertiary sort: Resource name
    return a.Resource.localeCompare(b.Resource);
  });

  // Get unique teams and roles for filter dropdowns (including functional roles)
  const uniqueTeams = [...new Set(combinedEntries.map(entry => entry.TeamName))].sort();
  const uniqueRoles = [...new Set(combinedEntries.map(entry => entry.RoleName))].sort();

  if (loading) {
    return (
      <div className="rbac-management">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading RBAC data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rbac-management">
      <div className="rbac-header">
        <div>
          <h2>Role Definition</h2>
          <p>Manage role-based access control for teams and systems</p>
        </div>
        <button 
          className="add-rbac-btn"
          onClick={() => setShowAddForm(true)}
        >
          <Plus size={16} />
          Add New Role Assignment
        </button>
      </div>

      {showAddForm && (
        <div className="rbac-form-overlay">
          <div className="rbac-form-container">
            <div className="form-header">
              <h3>Add New Role Assignment</h3>
              <button className="close-btn" onClick={handleCancel}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="rbac-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="resource">Resource *</label>
                  <SearchableDropdown
                    options={allResourceOptions}
                    value={formData.resource}
                    onChange={(value) => setFormData(prev => ({ ...prev, resource: value }))}
                    placeholder="Search existing resources or type new..."
                    allowCustom={true}
                    required={true}
                    id="resource"
                    name="resource"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="functionalRole">Functional Role (Optional)</label>
                  <SearchableDropdown
                    options={functionalRoleOptions}
                    value={formData.functionalRole}
                    onChange={(value) => setFormData(prev => ({ ...prev, functionalRole: value }))}
                    placeholder="Select a functional role..."
                    allowCustom={false}
                    id="functionalRole"
                    name="functionalRole"
                  />
                  <small className="form-hint">Choose a functional role to inherit its resource assignments</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="template">Template</label>
                  <input
                    type="text"
                    id="template"
                    name="template"
                    value={formData.template}
                    onChange={handleInputChange}
                    placeholder="e.g., LAN Access Request"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="expectedAccess">Expected Access</label>
                  <select
                    id="expectedAccess"
                    name="expectedAccess"
                    value={formData.expectedAccess}
                    onChange={handleInputChange}
                  >
                    <option value="Day 1">Day 1</option>
                    <option value="Week 1">Week 1</option>
                    <option value="Month 1">Month 1</option>
                    <option value="As Needed">As Needed</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="teamId">Team *</label>
                  <select
                    id="teamId"
                    name="teamId"
                    value={formData.teamId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Team</option>
                    {teams.map(team => (
                      <option key={team.TeamID} value={team.TeamID}>{team.Title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="roleId">Role *</label>
                  <select
                    id="roleId"
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Role</option>
                    {filteredRoles.map(role => (
                      <option key={role.RoleID} value={role.RoleID}>{role.Title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="systemId">System *</label>
                  <select
                    id="systemId"
                    name="systemId"
                    value={formData.systemId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select System</option>
                    {systems.map(system => (
                      <option key={system.id} value={system.id}>{system.Title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="newAD_Group"
                      checked={formData.newAD_Group}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    New AD Group
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dependencies">Dependencies</label>
                  <input
                    type="text"
                    id="dependencies"
                    name="dependencies"
                    value={formData.dependencies}
                    onChange={handleInputChange}
                    placeholder="Any dependencies"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="comments">Comments</label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  placeholder="Additional comments"
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="rbac-filters">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search by resource, team, role, system, or user..."
            className="search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <div className="filter-group">
            <Filter className="filter-icon" />
            <select
              className="filter-select"
              value={selectedTeam}
              onChange={e => setSelectedTeam(e.target.value)}
            >
              <option>All Teams</option>
              {uniqueTeams.map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>
          <select
            className="filter-select"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            <option>All Roles</option>
            {uniqueRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
          >
            <option>All Status</option>
            <option>Pending</option>
            <option>Approved</option>
            <option>Rejected</option>
          </select>
        </div>
      </div>

      <div className="rbac-table-container">
        <div className="table-header">
          <h3>Role Assignments ({filteredRbacEntries.length} of {combinedEntries.length})</h3>
          {(searchQuery || selectedTeam !== 'All Teams' || selectedRole !== 'All Roles' || selectedStatus !== 'All Status') && (
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchQuery('');
                setSelectedTeam('All Teams');
                setSelectedRole('All Roles');
                setSelectedStatus('All Status');
              }}
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>
        
        {filteredRbacEntries.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>
              {rbacEntries.length === 0 
                ? 'No RBAC entries found' 
                : 'No entries match your filters'
              }
            </h3>
            <p>
              {rbacEntries.length === 0 
                ? 'Click "Add New Role Assignment" to create your first entry.'
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="rbac-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Role</th>
                  <th>Resource</th>
                  <th>System</th>
                  <th>Template</th>
                  <th>Expected Access</th>
                  <th>Status</th>
                  <th>Modified Date</th>
                  <th>Modified By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRbacEntries.map(entry => (
                  <tr key={entry.id}>
                    <td>{entry.TeamName}</td>
                    <td>{entry.RoleName}</td>
                    <td className="resource-cell">{entry.Resource}</td>
                    <td>{entry.SystemName}</td>
                    <td>{entry.Template || '—'}</td>
                    <td>{entry.ExpectedAccess}</td>
                    <td>
                      <span className={getStatusClass(entry.Status)}>
                        {getStatusIcon(entry.Status)}
                        {entry.Status}
                      </span>
                    </td>
                    <td className="date-cell">{entry.ModifiedDate}</td>
                    <td>{entry.ModifiedBy}</td>
                    <td className="actions-cell">
                      {entry.Status === 'Pending' && (
                        <>
                          <button
                            className="action-btn approve-btn"
                            onClick={() => handleStatusUpdate(entry.id, 'Approved')}
                            title="Approve"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            className="action-btn reject-btn"
                            onClick={() => handleStatusUpdate(entry.id, 'Rejected')}
                            title="Reject"
                          >
                            <X size={14} />
                          </button>
                        </>
                      )}
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(entry.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default RBACManagement;
